import { z } from 'zod';
import { CounterpartySnapshotSchema } from '../counterparties/counterparties.schema.js';
import {
  HoldingUnitCapabilitiesSchema,
  UnitProfileTemplateKeySnapshotSchema,
} from '../holding-units/holding-units.schema.js';
import { InventoryTrackingModeSchema } from '../holding-units/holding-units-inventory-policy.schema.js';
import {
  IsoDateSchema,
  IsoDateTimeSchema,
  RcScopeTypeSchema,
} from '../shipments/shipments-types.js';
import { ReportableWeightLbSchema } from '../reportable-weight.schema.js';
import type {
  InboundReceiptDisplayDto,
  InboundReceiptDraftReceiving,
  InboundReceiptAttachmentSummary,
  InboundReceiptAuditEntryDto,
  InboundReceiptDetailDto,
  InboundReceiptDraft,
  InboundReceiptDraftRevalidation,
  InboundReceiptInventoryPostingUnitDto,
  InboundReceiptPostingUnitRegistrationDto,
  InboundReceiptPostingUnitRegistrationInput,
  InboundReceiptReceiverCompletionEvidenceSummary,
  InboundReceiptReceiverCompletionProvenance,
  InboundReceiptSourceDocument,
  ReceivingTicketRowInput,
  ReceivingTicketDto,
  ReceivingTicketRowDto,
} from './inbound-receipts-shared.dto.js';
import {
  inboundReceiptReceiverCompletionProvenanceKindValues,
  inboundReceiptReceiverCompletionStatusValues,
} from './inbound-receipts-shared.dto.js';
import {
  AttachmentAvailabilityStatusSchema,
  DEFAULT_INBOUND_RECEIPT_CONTAINER_KIND,
  DEFAULT_INBOUND_RECEIPT_IDENTITY_MODE,
  DEFAULT_INBOUND_RECEIPT_PRODUCT_KIND,
  DEFAULT_INBOUND_RECEIPT_UNIT_TYPE,
  DEFAULT_RECEIVING_GRANULARITY,
  InboundDraftRevalidationCodeSchema,
  InboundReceiptModeSchema,
  InboundReceiptPostingContainerKindSchema,
  InboundReceiptPostingIdentityModeSchema,
  InboundReceiptPostingProductKindSchema,
  InboundReceiptPostingUnitTypeSchema,
  InboundReceiptSourceEntryModeSchema,
  InboundReceiptSourceTypeSchema,
  InboundReceiptStatusSchema,
  InventoryPostingStatusSchema,
  OfficialComparisonInputStatusSchema,
  OfficialComparisonOutputStatusSchema,
  OfficialComparisonReviewStatusSchema,
  OfficialComparisonStatusSchema,
  OfficialComparisonStructuralStatusSchema,
  OfficialSettlementCaptureSourceSchema,
  PredictedSettlementPaymentBasisSchema,
  PredictedSettlementPredictionStatusSchema,
  PredictedSettlementToleranceStatusSchema,
  ReceivingGranularitySchema,
} from './inbound-receipts-types.js';

const trimmedStringSchema = z.string().trim().min(1);
const trimmedOptionalContentStringSchema = z.string().trim();
const nonNegativeNumberSchema = z.number().min(0);
const nonNegativeIntegerSchema = z.int().min(0);
const perLbRateSchema = z.number().min(0);

const InboundReceiptUnitProfileSnapshotSchema = {
  unitProfileId: z.uuid().nullable().optional(),
  unitProfileRevision: z.number().int().min(1).nullable().optional(),
  unitProfileDisplayName: trimmedStringSchema.max(120).nullable().optional(),
  unitProfileTemplateKey: UnitProfileTemplateKeySnapshotSchema.nullable().optional(),
  capabilityPolicy: HoldingUnitCapabilitiesSchema.nullable().optional(),
} as const;

export const ReceivingTicketRowInputSchema: z.ZodType<ReceivingTicketRowInput> = z
  .object({
    label: trimmedStringSchema.nullable().optional(),
    bagNumber: trimmedStringSchema.nullable().optional(),
    grossLb: nonNegativeNumberSchema,
    tareLb: nonNegativeNumberSchema,
    notes: trimmedStringSchema.nullable().optional(),
  })
  .strict();

export const InboundReceiptSourceDocumentSchema: z.ZodType<InboundReceiptSourceDocument> = z
  .object({
    shippingReportNumber: trimmedStringSchema.nullable().optional(),
    entryMode: InboundReceiptSourceEntryModeSchema.nullable().optional(),
    materialId: z.uuid(),
    materialTypeLabel: trimmedStringSchema,
    shipperCertificationId: trimmedStringSchema,
    shipperNameSnapshot: trimmedStringSchema,
    shipperAddressSnapshot: trimmedOptionalContentStringSchema,
    receiverCertificationId: trimmedStringSchema,
    receiverNameSnapshot: trimmedStringSchema,
    receiverAddressSnapshot: trimmedOptionalContentStringSchema,
    reportFrom: IsoDateSchema.nullable().optional(),
    reportThrough: IsoDateSchema.nullable().optional(),
    shippedRedemptionWeightLb: ReportableWeightLbSchema.nullable().optional(),
    shippedRefundValueCents: nonNegativeIntegerSchema.nullable().optional(),
    ratePerLb: perLbRateSchema.nullable().optional(),
    swLb: ReportableWeightLbSchema.nullable().optional(),
    scsCount: nonNegativeIntegerSchema.nullable().optional(),
    sclCount: nonNegativeIntegerSchema.nullable().optional(),
    spLb: ReportableWeightLbSchema.nullable().optional(),
    scrapRatePerLb: perLbRateSchema.nullable().optional(),
    redemptionWeightOverridden: z.boolean().nullable().optional(),
    refundValueOverridden: z.boolean().nullable().optional(),
  })
  .strict();

export const InboundReceiptDraftRevalidationSchema: z.ZodType<InboundReceiptDraftRevalidation> = z
  .object({
    isValid: z.boolean(),
    code: InboundDraftRevalidationCodeSchema.optional(),
    message: trimmedStringSchema.optional(),
  })
  .strict();

export const ReceivingTicketRowDtoSchema: z.ZodType<ReceivingTicketRowDto> = z
  .object({
    id: z.uuid(),
    sortOrder: z.int().min(0),
    label: trimmedStringSchema.nullable().optional(),
    bagNumber: trimmedStringSchema.nullable().optional(),
    grossLb: nonNegativeNumberSchema.nullable().optional(),
    tareLb: nonNegativeNumberSchema.nullable().optional(),
    netLb: nonNegativeNumberSchema,
    notes: trimmedStringSchema.nullable().optional(),
  })
  .strict();

export const ReceivingTicketDtoSchema: z.ZodType<ReceivingTicketDto> = z
  .object({
    id: z.uuid(),
    receiptId: z.uuid(),
    ticketNumber: trimmedStringSchema,
    materialId: z.uuid(),
    shipperCertificationId: trimmedStringSchema,
    receiverCertificationId: trimmedStringSchema,
    issuedAt: IsoDateTimeSchema,
    finalizedAt: IsoDateTimeSchema.nullable().optional(),
    measurementMode: InboundReceiptModeSchema,
    totalNetLb: nonNegativeNumberSchema,
    vehiclePlate: trimmedStringSchema.nullable().optional(),
    scaleLocation: trimmedStringSchema.nullable().optional(),
    createdByUserId: z.uuid(),
    finalizedByUserId: z.uuid().nullable().optional(),
    rows: z.array(ReceivingTicketRowDtoSchema),
  })
  .strict();

export const InboundReceiptAuditEntryDtoSchema: z.ZodType<InboundReceiptAuditEntryDto> = z
  .object({
    id: z.uuid(),
    action: trimmedStringSchema,
    occurredAt: IsoDateTimeSchema,
    actorUserId: z.uuid().nullable().optional(),
    notes: trimmedStringSchema.nullable().optional(),
  })
  .strict();

const InboundReceiptDisplayDifferenceValueSchema = z.union([
  trimmedStringSchema,
  z.number(),
  z.boolean(),
  z.null(),
]);

export const InboundReceiptReceiverCompletionProvenanceSchema: z.ZodType<InboundReceiptReceiverCompletionProvenance> =
  z
    .object({
      kind: z.enum(inboundReceiptReceiverCompletionProvenanceKindValues),
      sourceLabel: trimmedStringSchema,
      evidenceLabel: trimmedStringSchema,
      notDoriisUploadFile: z.literal(true),
      notDoriisSubmissionProofUnlessLinkedEvidence: z.literal(true),
      notes: z.array(trimmedStringSchema),
    })
    .strict();

export const InboundReceiptReceiverCompletionEvidenceSummarySchema: z.ZodType<InboundReceiptReceiverCompletionEvidenceSummary> =
  z
    .object({
      receivingTicketNumber: trimmedStringSchema.nullable(),
      weightTicketNumber: trimmedStringSchema.nullable(),
      doriisEvidenceLabel: trimmedStringSchema,
      linkedSourceReportEvidenceLabel: trimmedStringSchema.nullable(),
      evidenceNotes: z.array(trimmedStringSchema),
    })
    .strict();

export const InboundReceiptDisplayDtoSchema: z.ZodType<InboundReceiptDisplayDto> = z
  .object({
    headerSummary: z
      .object({
        receiptId: z.uuid(),
        receiptStatusCode: InboundReceiptStatusSchema,
        receiptStatusLabel: trimmedStringSchema,
        sourceTypeCode: InboundReceiptSourceTypeSchema,
        sourceTypeLabel: trimmedStringSchema,
        materialId: z.uuid(),
        materialLabel: trimmedStringSchema,
        shipperName: trimmedStringSchema,
        receiverName: trimmedStringSchema,
        confirmedAt: IsoDateTimeSchema,
        receivingTicketNumber: trimmedStringSchema.nullable(),
        transferReference: trimmedStringSchema.nullable(),
      })
      .strict(),
    coverStatus: z
      .object({
        statusCode: z.enum(['OFFICIAL_PENDING', 'READY_FOR_COVER', 'BLOCKED', 'CONSUMED']),
        label: z.enum(['Official pending', 'Ready for cover', 'Blocked', 'Consumed']),
        helpText: trimmedStringSchema,
      })
      .strict(),
    sourceSummary: z
      .object({
        entryMode: InboundReceiptSourceEntryModeSchema.nullable(),
        reportFrom: IsoDateSchema.nullable(),
        reportThrough: IsoDateSchema.nullable(),
        shippedRedemptionWeightLb: ReportableWeightLbSchema.nullable(),
        shippedRefundValueCents: nonNegativeIntegerSchema.nullable(),
        ratePerLb: perLbRateSchema.nullable(),
        swLb: ReportableWeightLbSchema.nullable(),
        scsCount: nonNegativeIntegerSchema.nullable(),
        sclCount: nonNegativeIntegerSchema.nullable(),
        spLb: ReportableWeightLbSchema.nullable(),
        scrapRatePerLb: perLbRateSchema.nullable(),
        redemptionWeightOverridden: z.boolean(),
        refundValueOverridden: z.boolean(),
      })
      .strict(),
    localPaymentsSummary: z
      .object({
        visible: z.boolean(),
        informationalOnly: z.boolean(),
        localSourceRefundValueCents: nonNegativeIntegerSchema.nullable(),
        localRatePerLb: perLbRateSchema.nullable(),
        scrapWeightLb: ReportableWeightLbSchema.nullable(),
        scrapValueCents: nonNegativeIntegerSchema.nullable(),
        helpText: trimmedStringSchema.nullable(),
      })
      .strict(),
    receivingSummary: z
      .object({
        receivedAt: IsoDateTimeSchema,
        transferReference: trimmedStringSchema.nullable(),
        ticketNumber: trimmedStringSchema.nullable(),
        vehiclePlate: trimmedStringSchema.nullable(),
        scaleLocation: trimmedStringSchema.nullable(),
        measurementMode: InboundReceiptModeSchema.nullable(),
        rowCount: nonNegativeIntegerSchema,
        totalReceivedWeightLb: nonNegativeNumberSchema,
        rows: z.array(ReceivingTicketRowDtoSchema),
        notes: trimmedStringSchema.nullable(),
      })
      .strict(),
    officialSettlementSummary: z
      .object({
        hasOfficialSettlement: z.boolean(),
        captureSource: OfficialSettlementCaptureSourceSchema.nullable(),
        shippingReportNumber: trimmedStringSchema.nullable(),
        receivedDate: IsoDateSchema.nullable(),
        weightTicketNumber: trimmedStringSchema.nullable(),
        receivedWeightLb: ReportableWeightLbSchema.nullable(),
        paymentWeightLb: ReportableWeightLbSchema.nullable(),
        shrinkagePercent: nonNegativeNumberSchema.nullable(),
        reductionFactor: nonNegativeNumberSchema.nullable(),
        refundValueCents: nonNegativeIntegerSchema.nullable(),
        processingFeeCents: nonNegativeIntegerSchema.nullable(),
        administrativeFeeCents: nonNegativeIntegerSchema.nullable(),
        totalPaymentDueCents: z.int().nullable(),
        onHold: z.boolean().nullable(),
        capturedAt: IsoDateTimeSchema.nullable(),
        importedAt: IsoDateTimeSchema.nullable(),
        activeOfficialRevisionId: z.uuid().nullable(),
        activeOfficialSnapshotId: z.uuid().nullable(),
      })
      .strict(),
    comparisonSummary: z
      .object({
        structuralStatus: OfficialComparisonStructuralStatusSchema,
        inputStatus: OfficialComparisonInputStatusSchema,
        outputStatus: OfficialComparisonOutputStatusSchema,
        comparisonStatus: OfficialComparisonStatusSchema,
        reviewStatus: OfficialComparisonReviewStatusSchema,
        message: trimmedStringSchema,
        showDifferenceTable: z.boolean(),
        showReviewAction: z.boolean(),
        differenceRows: z.array(
          z
            .object({
              label: trimmedStringSchema,
              appValue: InboundReceiptDisplayDifferenceValueSchema,
              officialValue: InboundReceiptDisplayDifferenceValueSchema,
            })
            .strict(),
        ),
      })
      .strict(),
    receiverCompletionSummary: z
      .object({
        documentTitle: z.literal('Receiver Completion'),
        status: z.enum(inboundReceiptReceiverCompletionStatusValues),
        completenessWarnings: z.array(trimmedStringSchema),
        provenance: InboundReceiptReceiverCompletionProvenanceSchema,
        evidenceSummary: InboundReceiptReceiverCompletionEvidenceSummarySchema,
      })
      .strict(),
    inventoryPostingSummary: z
      .object({
        visible: z.boolean(),
        canPostInventory: z.boolean(),
        trackingMode: InventoryTrackingModeSchema.nullable().optional(),
        postingStatus: InventoryPostingStatusSchema,
        inventoryStateLabel: trimmedStringSchema.optional(),
        helpText: trimmedStringSchema.optional(),
        actionLabel: trimmedStringSchema.nullable().optional(),
        autoCreated: z.boolean().optional(),
        postedWeightLb: nonNegativeNumberSchema.nullable(),
        postedAt: IsoDateTimeSchema.nullable(),
        postedByUserId: z.uuid().nullable(),
        createdHoldingUnitId: z.uuid().nullable(),
        reversedAt: IsoDateTimeSchema.nullable(),
        reversedByUserId: z.uuid().nullable(),
      })
      .strict(),
    printActions: z
      .object({
        canPrintReceivingTicket: z.boolean(),
        canPrintReceiptSummary: z.boolean(),
        canPrintReceiverCompletion: z.boolean(),
        canPrintOfficialSettlementSummary: z.boolean().optional(),
      })
      .strict(),
  })
  .strict();

export const InboundReceiptDraftReceivingSchema: z.ZodType<InboundReceiptDraftReceiving> = z
  .object({
    ticketId: z.uuid(),
    ticketNumber: trimmedStringSchema,
    receivedAt: IsoDateTimeSchema,
    vehiclePlate: trimmedStringSchema.nullable().optional(),
    scaleLocation: trimmedStringSchema.nullable().optional(),
    totalReceivedWeightLb: nonNegativeNumberSchema,
    measurementMode: InboundReceiptModeSchema,
    rows: z.array(ReceivingTicketRowInputSchema),
    notes: trimmedStringSchema.nullable().optional(),
  })
  .strict();

export const InboundReceiptOperationalDtoSchema = z
  .object({
    measurementMode: InboundReceiptModeSchema,
    receivedAt: IsoDateTimeSchema,
    receivingTicketId: z.uuid().nullable().optional(),
    internalTransferReference: trimmedStringSchema.nullable().optional(),
    receivedWeightLb: nonNegativeNumberSchema,
    unitRows: z.array(ReceivingTicketRowDtoSchema),
    notes: trimmedStringSchema.nullable().optional(),
  })
  .strict();

export const InboundReceiptDoriisInputDtoSchema = z
  .object({
    receivedDate: IsoDateSchema,
    weightTicketNumber: trimmedStringSchema,
    receivedWeightLb: ReportableWeightLbSchema,
    shrinkagePercent: nonNegativeNumberSchema.nullable().optional(),
  })
  .strict();

export const InboundReceiptPredictedSettlementDtoSchema = z
  .object({
    formulaVersion: trimmedStringSchema,
    predictionStatus: PredictedSettlementPredictionStatusSchema,
    reductionFactor: nonNegativeNumberSchema.nullable().optional(),
    toleranceStatus: PredictedSettlementToleranceStatusSchema,
    paymentBasis: PredictedSettlementPaymentBasisSchema,
    paymentWeightLb: ReportableWeightLbSchema.nullable().optional(),
    notes: trimmedStringSchema.nullable().optional(),
  })
  .strict();

export const InboundReceiptOfficialSettlementDtoSchema = z
  .object({
    captureSource: OfficialSettlementCaptureSourceSchema.nullable().optional(),
    shippingReportNumber: trimmedStringSchema.nullable().optional(),
    shippingReportNumberKey: trimmedStringSchema.nullable().optional(),
    receivedDate: IsoDateSchema.nullable().optional(),
    weightTicketNumber: trimmedStringSchema.nullable().optional(),
    receivedWeightLb: ReportableWeightLbSchema.nullable().optional(),
    shrinkagePercent: nonNegativeNumberSchema.nullable().optional(),
    reductionFactor: nonNegativeNumberSchema.nullable().optional(),
    paymentWeightLb: ReportableWeightLbSchema.nullable().optional(),
    refundValueCents: z.int().min(0).nullable().optional(),
    processingFeeCents: z.int().min(0).nullable().optional(),
    administrativeFeeCents: z.int().min(0).nullable().optional(),
    totalPaymentDueCents: z.int().nullable().optional(),
    onHold: z.boolean().nullable().optional(),
    capturedAt: IsoDateTimeSchema.nullable().optional(),
    capturedByUserId: z.uuid().nullable().optional(),
    importedAt: IsoDateTimeSchema.nullable().optional(),
    activeOfficialRevisionId: z.uuid().nullable().optional(),
    activeOfficialSnapshotId: z.uuid().nullable().optional(),
  })
  .strict();

export const InboundReceiptOfficialComparisonDtoSchema = z
  .object({
    structuralStatus: OfficialComparisonStructuralStatusSchema,
    inputStatus: OfficialComparisonInputStatusSchema,
    outputStatus: OfficialComparisonOutputStatusSchema,
    comparisonStatus: OfficialComparisonStatusSchema,
    reviewStatus: OfficialComparisonReviewStatusSchema,
    comparisonNotes: trimmedStringSchema.nullable().optional(),
    reviewedAt: IsoDateTimeSchema.nullable().optional(),
    reviewedByUserId: z.uuid().nullable().optional(),
  })
  .strict();

export const InboundReceiptInventoryPostingUnitDtoSchema: z.ZodType<InboundReceiptInventoryPostingUnitDto> =
  z
    .object({
      id: z.uuid(),
      sortOrder: z.int().min(0),
      receivingTicketRowId: z.uuid().nullable().optional(),
      materialId: z.uuid(),
      postedWeightLb: nonNegativeNumberSchema,
      ...InboundReceiptUnitProfileSnapshotSchema,
      unitType: InboundReceiptPostingUnitTypeSchema,
      identityMode: InboundReceiptPostingIdentityModeSchema,
      containerKind: InboundReceiptPostingContainerKindSchema,
      productKind: InboundReceiptPostingProductKindSchema,
      fixedIdAssetId: z.uuid().nullable().optional(),
      fixedIdAssetPhysicalId: trimmedStringSchema.nullable().optional(),
      fixedIdAssetExpectedVersion: z.int().min(0).nullable().optional(),
      createdHoldingUnitId: z.uuid(),
      label: trimmedStringSchema.nullable().optional(),
      bagNumber: trimmedStringSchema.nullable().optional(),
      notes: trimmedStringSchema.nullable().optional(),
    })
    .strict();

const InboundReceiptPostingUnitRegistrationInputObjectSchema = z
  .object({
    sortOrder: z.int().min(0),
    fixedIdAssetId: z.uuid().nullable().optional(),
    expectedFixedIdAssetVersion: z.int().min(0).nullable().optional(),
  })
  .strict();

export const InboundReceiptPostingUnitRegistrationInputSchema: z.ZodType<InboundReceiptPostingUnitRegistrationInput> =
  InboundReceiptPostingUnitRegistrationInputObjectSchema;

export const InboundReceiptPostingUnitRegistrationDtoSchema: z.ZodType<InboundReceiptPostingUnitRegistrationDto> =
  InboundReceiptPostingUnitRegistrationInputObjectSchema.extend({
    fixedIdAssetPhysicalId: trimmedStringSchema.nullable().optional(),
  }).strict();

export const InboundReceiptInventoryPostingDtoSchema = z
  .object({
    postingStatus: InventoryPostingStatusSchema,
    postedWeightLb: nonNegativeNumberSchema.nullable().optional(),
    postedAt: IsoDateTimeSchema.nullable().optional(),
    postedByUserId: z.uuid().nullable().optional(),
    createdHoldingUnitId: z.uuid().nullable().optional(),
    createdHoldingUnitIds: z.array(z.uuid()).optional(),
    postingUnits: z.array(InboundReceiptInventoryPostingUnitDtoSchema).optional(),
    reversedAt: IsoDateTimeSchema.nullable().optional(),
    reversedByUserId: z.uuid().nullable().optional(),
  })
  .strict();

export const InboundReceiptAttachmentConsumptionDtoSchema = z
  .object({
    availabilityStatus: AttachmentAvailabilityStatusSchema,
    consumedByCoverShipmentId: z.uuid().nullable().optional(),
    consumedAt: IsoDateTimeSchema.nullable().optional(),
  })
  .strict();

export const InboundReceiptDetailDtoSchema: z.ZodType<InboundReceiptDetailDto> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    receiverRcScopeType: RcScopeTypeSchema,
    receiverRcScopeId: z.uuid(),
    status: InboundReceiptStatusSchema,
    sourceType: InboundReceiptSourceTypeSchema,
    receivingGranularity: ReceivingGranularitySchema.default(DEFAULT_RECEIVING_GRANULARITY),
    ...InboundReceiptUnitProfileSnapshotSchema,
    unitType: InboundReceiptPostingUnitTypeSchema.default(DEFAULT_INBOUND_RECEIPT_UNIT_TYPE),
    identityMode: InboundReceiptPostingIdentityModeSchema.default(
      DEFAULT_INBOUND_RECEIPT_IDENTITY_MODE,
    ),
    containerKind: InboundReceiptPostingContainerKindSchema.default(
      DEFAULT_INBOUND_RECEIPT_CONTAINER_KIND,
    ),
    productKind: InboundReceiptPostingProductKindSchema.default(
      DEFAULT_INBOUND_RECEIPT_PRODUCT_KIND,
    ),
    postingUnitRegistrations: z.array(InboundReceiptPostingUnitRegistrationDtoSchema).optional(),
    sourceShipmentId: z.uuid().nullable().optional(),
    sourceDocument: InboundReceiptSourceDocumentSchema,
    operational: InboundReceiptOperationalDtoSchema,
    doriisInput: InboundReceiptDoriisInputDtoSchema,
    predictedSettlement: InboundReceiptPredictedSettlementDtoSchema,
    officialSettlement: InboundReceiptOfficialSettlementDtoSchema,
    officialComparison: InboundReceiptOfficialComparisonDtoSchema,
    inventoryPosting: InboundReceiptInventoryPostingDtoSchema,
    attachmentConsumption: InboundReceiptAttachmentConsumptionDtoSchema,
    audit: z
      .object({
        entries: z.array(InboundReceiptAuditEntryDtoSchema),
      })
      .strict(),
    createdAt: IsoDateTimeSchema,
    createdByUserId: z.uuid(),
    confirmedAt: IsoDateTimeSchema,
    confirmedByUserId: z.uuid(),
    canceledAt: IsoDateTimeSchema.nullable().optional(),
    canceledByUserId: z.uuid().nullable().optional(),
    display: InboundReceiptDisplayDtoSchema.optional(),
  })
  .strict();

export const InboundReceiptDraftSchema: z.ZodType<InboundReceiptDraft> = z
  .object({
    draftId: z.uuid(),
    companyId: z.uuid(),
    stationId: z.uuid(),
    sourceType: InboundReceiptSourceTypeSchema,
    mode: InboundReceiptModeSchema,
    receivingGranularity: ReceivingGranularitySchema.default(DEFAULT_RECEIVING_GRANULARITY),
    ...InboundReceiptUnitProfileSnapshotSchema,
    unitType: InboundReceiptPostingUnitTypeSchema.default(DEFAULT_INBOUND_RECEIPT_UNIT_TYPE),
    identityMode: InboundReceiptPostingIdentityModeSchema.default(
      DEFAULT_INBOUND_RECEIPT_IDENTITY_MODE,
    ),
    containerKind: InboundReceiptPostingContainerKindSchema.default(
      DEFAULT_INBOUND_RECEIPT_CONTAINER_KIND,
    ),
    productKind: InboundReceiptPostingProductKindSchema.default(
      DEFAULT_INBOUND_RECEIPT_PRODUCT_KIND,
    ),
    postingUnitRegistrations: z.array(InboundReceiptPostingUnitRegistrationDtoSchema).optional(),
    materialId: z.uuid(),
    sourceShipmentCompanyId: z.uuid().nullable(),
    sourceShipmentId: z.uuid().nullable(),
    shipper: CounterpartySnapshotSchema,
    receiver: CounterpartySnapshotSchema,
    sourceDocument: InboundReceiptSourceDocumentSchema,
    transferReference: trimmedStringSchema.nullable(),
    receiving: InboundReceiptDraftReceivingSchema,
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    revalidation: InboundReceiptDraftRevalidationSchema,
  })
  .strict();

export const InboundReceiptAttachmentSummarySchema: z.ZodType<InboundReceiptAttachmentSummary> = z
  .object({
    receiptId: z.uuid(),
    materialId: z.uuid(),
    receiverRcScopeType: RcScopeTypeSchema,
    receiverRcScopeId: z.uuid(),
    sourceType: InboundReceiptSourceTypeSchema,
    shipper: CounterpartySnapshotSchema,
    receiver: CounterpartySnapshotSchema,
    shippingReportNumber: trimmedStringSchema,
    receivedWeightLb: nonNegativeNumberSchema,
    paymentWeightLb: ReportableWeightLbSchema.nullable(),
    refundValueCents: z.number().int().min(0).nullable(),
    receivedAt: IsoDateTimeSchema,
    internalTransferReference: trimmedStringSchema.nullable(),
    availabilityStatus: AttachmentAvailabilityStatusSchema,
    comparisonStatus: OfficialComparisonStatusSchema,
    reviewStatus: OfficialComparisonReviewStatusSchema,
    confirmedAt: IsoDateTimeSchema,
    consumedByCoverShipmentId: z.uuid().nullable(),
  })
  .strict();
